import { useEffect } from 'react'

import { FixedBanner } from '@circlefin/components'
import {
  AdminApprovalsSettingsDocument,
  ManageableUsersDocument,
  useVoteOnProposalMutation,
} from '@shared/graphql'
import { useGraphQLError } from '@shared/hooks/common'
import useTranslation from 'next-translate/useTranslation'

export interface EmailApprovalBannerProps {
  /**
   * Proposal ID.
   */
  proposalId?: string
  /**
   * Is vote an approval or a rejection.
   */
  isApproved?: boolean
  /**
   * Custom style.
   */
  className?: string
}

export const EmailApprovalBanner = ({
  proposalId,
  isApproved,
  className,
}: EmailApprovalBannerProps) => {
  const { getError } = useGraphQLError()
  const { t } = useTranslation('custody/common')
  const [submitVote, { called, error, loading }] = useVoteOnProposalMutation({
    refetchQueries: [
      { query: ManageableUsersDocument },
      { query: AdminApprovalsSettingsDocument },
    ],
    awaitRefetchQueries: true,
    // The Error is handled by the banner itself to show users the failed submission
    onError: () => null,
  })

  useEffect(() => {
    if (
      // Only submit a vote once
      !called &&
      proposalId != null &&
      isApproved != null
    ) {
      void submitVote({
        variables: {
          input: {
            proposalId,
            isApproved,
          },
        },
      })
    }
  }, [called, isApproved, proposalId, submitVote])

  if (!called || loading) {
    return null
  }

  return (
    <FixedBanner className={className} status={error ? 'error' : 'success'}>
      <FixedBanner.Title>
        {t(`emailApproval.title.${error ? 'error' : 'success'}`)}
      </FixedBanner.Title>
      <FixedBanner.Description>
        {error ? getError(error) : t('emailApproval.description')}
      </FixedBanner.Description>
    </FixedBanner>
  )
}
